import React, { useEffect, useState } from "react";
import { Play, Pause, Loader2 } from "lucide-react";

const START_EVENT = "afwn-voice-start";
let current = null;

const stopCurrent = () => {
  if (!current) return;
  current.pause();
  current.currentTime = 0;
  current = null;
};

const startClip = (slug, setState) => {
  stopCurrent();
  document.dispatchEvent(new CustomEvent(START_EVENT, { detail: slug }));
  const audio = new Audio(`/voices/${slug}.mp3`);
  current = audio;
  const done = () => { setState("idle"); if (current === audio) current = null; };
  audio.onplaying = () => setState("playing");
  audio.onended = done;
  audio.onerror = done;
  setState("loading");
  audio.play().catch(done);
  return audio;
};

const useVoiceClip = (slug) => {
  const [state, setState] = useState("idle"); // idle | loading | playing

  useEffect(() => {
    const onOther = (e) => { if (e.detail !== slug) setState("idle"); };
    document.addEventListener(START_EVENT, onOther);
    return () => document.removeEventListener(START_EVENT, onOther);
  }, [slug]);

  useEffect(() => () => { if (current?.src.endsWith(`/voices/${slug}.mp3`)) stopCurrent(); }, [slug]);

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (state !== "idle") { stopCurrent(); setState("idle"); return; }
    startClip(slug, setState);
  };
  return { state, toggle };
};

const Bars = ({ color }) => (
  <span className="flex items-end gap-[2px] h-3.5" aria-hidden>
    {["a", "b", "c", "d"].map((id, i) => (
      <span key={id} className="w-[3px] rounded-full afwn-bar" style={{ background: color || "currentColor", animationDelay: `${i * 0.12}s` }} />
    ))}
  </span>
);

const Glyph = ({ state, color, size }) => {
  if (state === "playing") return <Bars color={color} />;
  if (state === "loading") return <Loader2 size={size} className="animate-spin fill-current" />;
  return <Play size={size} className="ml-0.5 fill-current" />;
};

const PILL_LABEL = { idle: "Hear my voice", loading: "Loading…", playing: "Playing…" };

export const VoiceButton = ({ slug, name, color, variant = "pill", className = "" }) => {
  const { state, toggle } = useVoiceClip(slug);
  const label = state === "playing" ? `Pause ${name}'s voice` : `Hear ${name}'s voice`;
  const shared = { type: "button", onClick: toggle, "aria-label": label, "data-testid": `voice-play-${slug}`, "data-state": state };

  if (variant === "icon") {
    return (
      <button {...shared} title={label} style={{ color }}
        className={`w-10 h-10 rounded-full bg-white/95 backdrop-blur shadow-md flex items-center justify-center transition-all duration-300 hover:scale-110 focus:outline-none focus-visible:ring-2 ring-white ${className}`}>
        <Glyph state={state} color={color} size={16} />
      </button>
    );
  }
  return (
    <button {...shared} style={{ color, borderColor: `${color}55` }}
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium bg-white transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 focus:outline-none ${className}`}>
      <Glyph state={state} color={color} size={15} />
      {PILL_LABEL[state]}
    </button>
  );
};
